import { LoadingState } from '../../components/feedback/LoadingState'
import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { pageTheme, headerTheme, contentTheme, subTheme, cardTheme } from '../../components/consoleTheme'

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Plus, Database, Trash2, RefreshCw, Server } from "lucide-react"

const API = "/api/db"
function getToken() {
  return sessionStorage.getItem("token") || localStorage.getItem("token")
}
function authHeaders(json = true) {
  const h = {}
  if (json) h["Content-Type"] = "application/json"
  const t = getToken()
  if (t) h["Authorization"] = `Bearer ${t}`
  return h
}

const PageContainer = styled.div`
  ${pageTheme}
padding: 0; background: #f8fafc; min-height: 100vh;`
const Header = styled.header`
  ${headerTheme}

  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 2rem; background: #fff; border-bottom: 1px solid #e2e8f0;
  @media(max-width:768px){padding:1rem;flex-wrap:wrap;gap:.75rem;align-items:flex-start;}
`
const Title = styled.h2`font-size: 18px; font-weight: 600; color: #1e293b; margin:0;`
const Sub = styled.p`
  ${subTheme}
margin: .25rem 0 0; font-size: .8rem; color: #94a3b8;`
const Button = styled.button`
  display: inline-flex; align-items: center; gap: .5rem; padding: .5rem 1rem;
  border-radius: .375rem; font-weight: 500; cursor: pointer;
  ${(p) => p.primary
    ? `background:var(--accent);color:#fff;border:none;&:hover{background:#115855;}`
    : `background:#fff;color:#64748b;border:1px solid #e2e8f0;&:hover{background:#f8fafc;}`}
`
const Content = styled.div`
  ${contentTheme}
padding: 2rem; @media(max-width:768px){padding:1rem;}`
const Card = styled.div`
  ${cardTheme}
background:#fff; border:1px solid #e2e8f0; border-radius:.5rem; overflow:hidden; margin-bottom:1.5rem;`
const RowHead = styled.div`
  display:grid; grid-template-columns: 1.1fr 1fr 1.5fr .65fr .75fr .55fr .7fr .85fr; gap:.5rem;
  padding:.85rem 1.5rem; border-bottom:1px solid #e2e8f0; font-weight:600; color:#475569; font-size:.85rem;
  @media(max-width:768px){display:none;}
`
const Row = styled.div`
  display:grid; grid-template-columns: 1.1fr 1fr 1.5fr .65fr .75fr .55fr .7fr .85fr; gap:.5rem;
  padding:.9rem 1.5rem; border-bottom:1px solid #f1f5f9; align-items:center;
  &:last-child{border-bottom:none;}
  @media(max-width:768px){
    grid-template-columns:minmax(0,1fr) minmax(0,1fr);
    gap:.6rem 1rem;padding:1rem;border-bottom:8px solid #f8fafc;
    > *{min-width:0;overflow-wrap:anywhere;}
    > :first-child, > :nth-child(3), > :last-child{grid-column:1/-1;}
    > :last-child{display:flex;flex-wrap:wrap;gap:.4rem;}
    > [data-label]::before{content:attr(data-label);display:block;margin-bottom:.2rem;color:#94a3b8;font-size:.7rem;font-weight:600;}
  }
`
const Mono = styled.code`font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.8rem; color:#334155;`
const Dot = styled.span`display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:6px;
  background:${(p)=>p.s==="ok"?"#22c55e":p.s==="fail"?"#ef4444":"#cbd5e1"};`
const IconBtn = styled.button`
  background:none;border:1px solid #e2e8f0;border-radius:6px;padding:4px 8px;cursor:pointer;color:#64748b;
  display:inline-flex;align-items:center;gap:4px;font-size:.78rem;&:hover{color:var(--accent);border-color:var(--accent);}
`
const Empty = styled.div`padding:2rem;text-align:center;color:#94a3b8;`
const Form = styled.div`
  display:grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap:.5rem;
  padding:1rem 1.5rem; border-bottom:1px solid #f1f5f9; align-items:center;
  input{padding:.5rem .7rem;border:1px solid #e2e8f0;border-radius:6px;outline:none;font-size:.85rem;
    &:focus{border-color:var(--accent);}}
  label{font-size:.8rem;color:#475569;display:flex;align-items:center;gap:6px;}
  @media(max-width:768px){grid-template-columns:minmax(0,1fr);padding:1rem;input{min-width:0;width:100%;}}
`

const DatabaseDashboard = () => {
  const [conns, setConns] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState("")
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(null)
  const emptyForm = { name: "", host: "", port: "5432", dbname: "", username: "", password: "", ssl: false, peer_id: "" }
  const [form, setForm] = useState(emptyForm)
  const [peers, setPeers] = useState([])

  const load = async () => {
    setLoading(true); setErr("")
    try {
      const res = await fetch(`${API}/connections`, { headers: authHeaders(false), credentials: "include" })
      if (res.status === 401 || res.status === 403) { setErr("로그인이 필요합니다."); setConns([]); return }
      const data = await res.json()
      setConns(data.connections || [])
    } catch (e) { setErr("목록을 불러오지 못했습니다.") }
    finally { setLoading(false) }
    try { const pr = await fetch(`/api/vpn/peers`, { headers: authHeaders(false), credentials: "include" }).then(x => x.json()); setPeers(pr.peers || []) } catch (_) {}
  }
  useEffect(() => { load() }, [])

  const create = async () => {
    if (!form.name || !form.host || !form.dbname || !form.username) {
      alert("이름·호스트·DB명·계정은 필수입니다."); return
    }
    setBusy("new")
    try {
      const res = await fetch(`${API}/connections`, {
        method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify(form),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { alert("등록 실패: " + (data.error || res.status)); return }
      if (data.test && !data.test.ok) alert("등록은 됐지만 연결 테스트 실패:\n" + data.test.error)
      setForm(emptyForm)
      setCreating(false); load()
    } catch (e) { alert("등록 실패: " + e.message) }
    finally { setBusy(null) }
  }

  const test = async (id) => {
    setBusy(id)
    try {
      const res = await fetch(`${API}/connections/${id}/test`, { method: "POST", headers: authHeaders(false), credentials: "include" })
      const r = await res.json().catch(() => ({}))
      if (!r.ok) alert("연결 실패:\n" + (r.error || "알 수 없는 오류"))
      load()
    } catch (e) { alert("테스트 실패: " + e.message) }
    finally { setBusy(null) }
  }

  const del = async (id) => {
    if (!window.confirm("이 DB 연결을 삭제할까요?")) return
    try {
      await fetch(`${API}/connections/${id}`, { method: "DELETE", headers: authHeaders(false), credentials: "include" })
      load()
    } catch (e) {}
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }))

  return (
    <PageContainer>
      <Header>
        <div>
          <Title>PostgreSQL 연결</Title>
          <Sub>외부·온프레(VPC) PostgreSQL을 등록하고 연결 상태를 관리합니다.</Sub>
        </div>
        <div style={{ display: "flex", gap: ".5rem" }}>
          <Button onClick={load}><RefreshCw size={16} /> 새로고침</Button>
          <Button primary onClick={() => setCreating((v) => !v)}><Plus size={16} /> DB 연결 추가</Button>
        </div>
      </Header>

      <Content>
        <Card>
          {creating && (
            <>
              <Form>
                <input placeholder="이름 (예: 운영 PG)" value={form.name} onChange={set("name")} />
                <input placeholder="호스트 (예: antsnest.sldb.iwinv.net)" value={form.host} onChange={set("host")} />
                {peers.length > 0 && (
                  <select value={String(form.peer_id || "")} title="인스턴스를 고르면 그 피어의 가상IP가 호스트로 들어가고, 등록이 그 인스턴스에 묶인다"
                    onChange={(e) => {
                      const pid = e.target.value
                      const p = peers.find((x) => String(x.peer_id) === pid)
                      setForm((f) => ({ ...f, peer_id: pid, host: p ? p.virtual_ip : f.host }))
                    }}
                    style={{ padding: ".5rem .7rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: ".85rem", outline: "none" }}>
                    <option value="">인스턴스 선택(미지정 = 외부 PG)…</option>
                    {peers.map((p) => <option key={p.peer_id} value={p.peer_id}>{p.hostname} · {p.virtual_ip}</option>)}
                  </select>
                )}
                <input placeholder="포트" value={form.port} onChange={set("port")} style={{ maxWidth: 90 }} />
                <input placeholder="DB명" value={form.dbname} onChange={set("dbname")} />
                <input placeholder="계정" value={form.username} onChange={set("username")} />
                <input placeholder="비밀번호" type="password" value={form.password} onChange={set("password")} />
                <label><input type="checkbox" checked={form.ssl} onChange={set("ssl")} /> SSL</label>
              </Form>
              <div style={{ display: "flex", gap: ".5rem", padding: "0 1.5rem 1rem" }}>
                <Button primary onClick={create} disabled={busy === "new"}>
                  {busy === "new" ? "테스트·등록 중…" : "연결 테스트 후 등록"}
                </Button>
                <Button onClick={() => setCreating(false)}>취소</Button>
              </div>
            </>
          )}

          <RowHead>
            <span>이름</span><span>인스턴스</span><span>대상 (host / db)</span><span>상태</span><span>커넥션</span><span>테이블</span><span>크기</span><span>동작</span>
          </RowHead>

          {loading ? <LoadingState label="불러오는 중…" />
            : err ? <Empty>{err}</Empty>
            : conns.length === 0 ? <Empty>등록된 DB 연결이 없습니다. “DB 연결 추가”로 외부 PostgreSQL을 연결하세요.</Empty>
            : conns.map((c) => (
              <Row key={c.conn_id}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "#1e293b" }}>
                  <Database size={16} color="#64748b" /> {c.name}
                </span>
                <span data-label="인스턴스" style={{ fontSize: ".82rem", color: c.peer_id ? "#334155" : "#94a3b8" }}
                  title={c.peer_id ? `${c.peer_mesh_name || ""} · ${c.peer_ip || ""}` : "인스턴스 미지정 (외부 PG)"}>
                  {c.peer_id ? (<><Server size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />{c.peer_hostname || c.peer_ip}</>) : "외부"}
                </span>
                <Mono data-label="대상">{c.host}:{c.port} / {c.dbname}{c.ssl ? " (SSL)" : ""}</Mono>
                <span data-label="상태" style={{ color: "#475569" }} title={c.last_error || ""}>
                  <Dot s={c.last_status} />
                  {c.last_status === "ok" ? "연결됨" : c.last_status === "fail" ? "실패" : "미확인"}
                </span>
                <span data-label="커넥션" style={{ fontSize: ".82rem", color: "#475569" }} title="현재 활성 / 최대 연결(커넥션 풀)">
                  {c.last_active != null ? (
                    <>
                      <strong style={{ color: c.last_max && c.last_active / c.last_max > 0.8 ? "#ef4444" : "var(--accent)" }}>{c.last_active}</strong>
                      <span style={{ color: "#94a3b8" }}> / {c.last_max ?? "?"}</span>
                    </>
                  ) : "-"}
                </span>
                <span data-label="테이블" style={{ fontSize: ".82rem", color: "#475569" }} title="테이블 수">
                  {c.last_tables != null ? c.last_tables : "-"}
                </span>
                <span data-label="크기" style={{ fontSize: ".78rem", color: "#64748b" }} title={c.last_version || ""}>
                  {c.last_size || "-"}
                </span>
                <span style={{ display: "flex", gap: 6 }}>
                  <IconBtn onClick={() => test(c.conn_id)} disabled={busy === c.conn_id}>
                    {busy === c.conn_id ? "…" : (<><Server size={13} /> 테스트</>)}
                  </IconBtn>
                  <IconBtn style={{ borderColor: "#fecaca", color: "#ef4444" }} onClick={() => del(c.conn_id)} title="삭제">
                    <Trash2 size={13} />
                  </IconBtn>
                </span>
              </Row>
            ))}
        </Card>
      </Content>
    </PageContainer>
  )
}

export default DatabaseDashboard
